import React, { useState, useEffect, useCallback } from 'react';
import { testBankApi, questionsApi, coursesApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Plus, Trash, MagnifyingGlass, Pencil, ClipboardText, Question,
  CheckCircle, Eye, ArrowLeft, BookOpen
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import ProcessGuide from '../../components/ProcessGuide';

export default function TestBankPage() {
  const [tests, setTests] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCourseId, setFilterCourseId] = useState('');
  const [mandanteFilter, setMandanteFilter] = useState('all');
  const [formMandante, setFormMandante] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [testQuestions, setTestQuestions] = useState([]);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [questionSearch, setQuestionSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteDeps, setDeleteDeps] = useState(null);
  const [loadingDeps, setLoadingDeps] = useState(false);
  const [formData, setFormData] = useState({
    name: '', description: '', course_id: '',
    time_limit_minutes: 30, passing_percentage: 70, max_attempts: 3, questions_per_test: 0
  });

  const fetchTests = useCallback(async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (filterCourseId) params.course_id = filterCourseId;
      const res = await testBankApi.getAll(params);
      setTests(res.data);
    } catch { toast.error('Error al cargar tests'); }
    finally { setLoading(false); }
  }, [searchTerm, filterCourseId]);

  const fetchCourses = async () => {
    try {
      const res = await coursesApi.getAll();
      setCourses(res.data);
    } catch {}
  };

  useEffect(() => { fetchTests(); }, [fetchTests]);
  useEffect(() => { fetchCourses(); }, []);

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();
    if (!formData.course_id) {
      toast.error('Debe seleccionar un curso');
      return;
    }
    try {
      if (editingTest) {
        await testBankApi.update(editingTest.id, formData);
        toast.success('Test actualizado');
      } else {
        await testBankApi.create(formData);
        toast.success('Test creado');
      }
      setShowCreateDialog(false);
      resetForm();
      fetchTests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (test) => {
    setDeleteTarget(test);
    setLoadingDeps(true);
    try {
      const res = await testBankApi.getDependencies(test.id);
      setDeleteDeps(res.data);
    } catch {
      setDeleteDeps({ questions_count: 0, courses: [], courses_count: 0, evaluations_count: 0, has_dependencies: false });
    } finally {
      setLoadingDeps(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await testBankApi.delete(deleteTarget.id);
      toast.success('Test eliminado');
      if (selectedTest?.id === deleteTarget.id) setSelectedTest(null);
      fetchTests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleteTarget(null);
      setDeleteDeps(null);
    }
  };

  const openEdit = (test) => {
    setEditingTest(test);
    const course = courses.find(c => c.id === test.course_id);
    setFormMandante(course?.client_id || 'all');
    setFormData({
      name: test.name, description: test.description || '',
      course_id: test.course_id || '',
      time_limit_minutes: test.time_limit_minutes,
      passing_percentage: test.passing_percentage,
      max_attempts: test.max_attempts,
      questions_per_test: test.questions_per_test || 0
    });
    setShowCreateDialog(true);
  };

  const resetForm = () => {
    setEditingTest(null);
    setFormMandante('all');
    setFormData({ name: '', description: '', course_id: '', time_limit_minutes: 30, passing_percentage: 70, max_attempts: 3, questions_per_test: 0 });
  };

  const openTestDetail = async (test) => {
    setSelectedTest(test);
    setQuestionSearch('');
    try {
      const [qRes, avRes] = await Promise.all([
        testBankApi.getQuestions(test.id),
        testBankApi.getAvailableQuestions(test.id)
      ]);
      setTestQuestions(qRes.data);
      setAvailableQuestions(avRes.data);
    } catch { toast.error('Error al cargar preguntas'); }
  };

  const handleAddQuestion = async (questionId) => {
    try {
      await testBankApi.addQuestion(selectedTest.id, questionId);
      toast.success('Pregunta agregada');
      const [qRes, avRes] = await Promise.all([
        testBankApi.getQuestions(selectedTest.id),
        testBankApi.getAvailableQuestions(selectedTest.id)
      ]);
      setTestQuestions(qRes.data);
      setAvailableQuestions(avRes.data);
      fetchTests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleRemoveQuestion = async (questionId) => {
    try {
      await testBankApi.removeQuestion(selectedTest.id, questionId);
      toast.success('Pregunta removida');
      const [qRes, avRes] = await Promise.all([
        testBankApi.getQuestions(selectedTest.id),
        testBankApi.getAvailableQuestions(selectedTest.id)
      ]);
      setTestQuestions(qRes.data);
      setAvailableQuestions(avRes.data);
      fetchTests();
    } catch { toast.error('Error'); }
  };

  const filteredAvailable = availableQuestions.filter(q =>
    questionSearch === '' || q.question_text.toLowerCase().includes(questionSearch.toLowerCase())
  );

  const mandantes = [...new Map(courses.filter(c => c.client_id).map(c => [c.client_id, c.client_name || 'Sin mandante'])).entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const courseClientMap = Object.fromEntries(courses.map(c => [c.id, c.client_id]));
  const visibleTests = tests.filter(t => mandanteFilter === 'all' || courseClientMap[t.course_id] === mandanteFilter);
  const dialogCourses = courses.filter(c => formMandante === 'all' || c.client_id === formMandante);
  const filterCourses = courses.filter(c => mandanteFilter === 'all' || c.client_id === mandanteFilter);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" /></div>;
  }

  // Detail view
  if (selectedTest) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="test-bank-detail">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => setSelectedTest(null)} className="text-[#2B4B8C]/60 hover:text-[#2B4B8C] -ml-2" data-testid="back-to-tests">
            <ArrowLeft size={20} className="mr-1" /> Volver al Banco
          </Button>
        </div>

        <div>
          <h1 className="text-2xl font-bold text-[#2B4B8C]">{selectedTest.name}</h1>
          <div className="flex items-center gap-2 mt-1">
            <BookOpen size={14} className="text-[#2BBAA4]" />
            <span className="text-sm text-[#2BBAA4] font-medium">{selectedTest.course_name || 'Sin curso'}</span>
          </div>
          <p className="text-[#2B4B8C]/50 text-sm mt-1">
            {selectedTest.time_limit_minutes} min &middot; {selectedTest.passing_percentage}% aprobación &middot; {selectedTest.max_attempts} intentos
          </p>
          {selectedTest.description && <p className="text-[#2B4B8C]/60 text-sm mt-1">{selectedTest.description}</p>}
        </div>

        {/* Assigned questions */}
        <div>
          <h2 className="text-lg font-semibold text-[#2B4B8C] mb-3">Preguntas del Banco ({testQuestions.length})</h2>
          {testQuestions.length === 0 ? (
            <Card className="bg-white border-[#2B4B8C]/10">
              <CardContent className="text-center py-8 text-[#2B4B8C]/40">
                No hay preguntas asignadas a este banco
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {testQuestions.map((q) => (
                <div key={q.id} className="flex items-center justify-between p-3 rounded-lg bg-green-50 border border-green-200">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <CheckCircle size={16} className="text-green-500 shrink-0" />
                    <span className="text-sm text-[#2B4B8C] truncate">{q.question_text}</span>
                    <Badge className="text-xs bg-[#2B4B8C]/10 text-[#2B4B8C] border-[#2B4B8C]/20 border shrink-0">
                      {q.question_type === 'multiple_choice' ? 'Opción Múltiple' : 'V/F'}
                    </Badge>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleRemoveQuestion(q.id)} className="text-red-400 hover:text-red-500 shrink-0" data-testid={`remove-q-${q.id}`}>
                    <Trash size={14} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add questions - filtered by test's course */}
        <div>
          <h2 className="text-lg font-semibold text-[#2B4B8C] mb-1">Agregar Preguntas</h2>
          <p className="text-xs text-[#2B4B8C]/50 mb-3">
            Solo se muestran preguntas del curso: <span className="font-medium text-[#2BBAA4]">{selectedTest.course_name || 'Sin curso'}</span>
          </p>
          <div className="relative mb-3">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/60" size={18} />
            <Input
              placeholder="Buscar preguntas..."
              value={questionSearch}
              onChange={(e) => setQuestionSearch(e.target.value)}
              className="pl-10 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
              data-testid="search-available-questions"
            />
          </div>
          {filteredAvailable.length === 0 ? (
            <Card className="bg-white border-[#2B4B8C]/10">
              <CardContent className="text-center py-6 text-[#2B4B8C]/40 text-sm">
                No hay más preguntas disponibles para este curso
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredAvailable.map((q) => (
                <div key={q.id} className="flex items-center justify-between p-3 rounded-lg bg-[#f8fafc] border border-[#2B4B8C]/10">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Question size={16} className="text-[#2B4B8C]/40 shrink-0" />
                    <span className="text-sm text-[#2B4B8C] truncate">{q.question_text}</span>
                    <Badge className="text-xs bg-[#2B4B8C]/10 text-[#2B4B8C] border-[#2B4B8C]/20 border shrink-0">
                      {q.question_type === 'multiple_choice' ? 'Opción Múltiple' : 'V/F'}
                    </Badge>
                  </div>
                  <Button size="sm" onClick={() => handleAddQuestion(q.id)} className="bg-[#2BBAA4] text-black hover:bg-[#239986] h-7 text-xs shrink-0" data-testid={`add-q-${q.id}`}>
                    <Plus size={14} className="mr-1" /> Agregar
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main list view
  return (
    <div className="space-y-6 animate-fade-in" data-testid="test-bank-page">
      <ProcessGuide activeStep={3} />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2B4B8C]">Banco de Preguntas</h1>
          <p className="text-[#2B4B8C]/60">Arma bancos de preguntas por curso para asignar a las capacitaciones</p>
        </div>
        <Button onClick={() => { resetForm(); setShowCreateDialog(true); }} className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="create-test-btn">
          <Plus size={20} className="mr-2" /> Nuevo Banco
        </Button>
      </div>

      {/* Search + Filter */}
      <Card className="bg-white border-[#2B4B8C]/10">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/60" size={20} />
              <Input
                placeholder="Buscar banco..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                data-testid="search-tests"
              />
            </div>
            <Select value={mandanteFilter} onValueChange={(v) => { setMandanteFilter(v); setFilterCourseId(''); }}>
              <SelectTrigger className="w-full sm:w-56 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="filter-by-mandante">
                <SelectValue placeholder="Filtrar por mandante" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#2B4B8C]/10 max-h-64">
                <SelectItem value="all">Todos los mandantes</SelectItem>
                {mandantes.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCourseId} onValueChange={setFilterCourseId}>
              <SelectTrigger className="w-full sm:w-56 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="filter-by-course">
                <SelectValue placeholder="Filtrar por curso" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#2B4B8C]/10 max-h-64">
                <SelectItem value="all">Todos los cursos</SelectItem>
                {filterCourses.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tests List */}
      <div className="space-y-3">
        {visibleTests.map((test) => (
          <Card key={test.id} className="bg-white border-[#2B4B8C]/10 hover:border-[#2BBAA4]/30 transition-colors" data-testid={`test-card-${test.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1 cursor-pointer" onClick={() => openTestDetail(test)}>
                  <div className="flex items-center gap-2 mb-1">
                    <ClipboardText size={20} className="text-[#2BBAA4]" />
                    <h3 className="font-medium text-[#2B4B8C]">{test.name}</h3>
                    {!test.is_active && <Badge className="bg-red-500/10 text-red-400 border-red-500/20 border text-xs">Inactivo</Badge>}
                  </div>
                  {test.course_name && (
                    <div className="flex items-center gap-1.5 ml-7 mb-1">
                      <BookOpen size={14} className="text-[#2B4B8C]/40" />
                      <span className="text-xs text-[#2B4B8C]/60">Curso: <span className="font-medium text-[#2B4B8C]/80">{test.course_name}</span></span>
                    </div>
                  )}
                  {test.description && <p className="text-xs text-[#2B4B8C]/50 mb-2 ml-7">{test.description}</p>}
                  <div className="flex gap-4 text-xs text-[#2B4B8C]/60 ml-7">
                    <span>{test.time_limit_minutes} min</span>
                    <span>{test.passing_percentage}% aprobación</span>
                    <span>{test.max_attempts} intentos</span>
                    <span className="text-[#2BBAA4] font-medium">{test.questions_count} preguntas</span>
                    {test.questions_per_test > 0 && (
                      <span className="text-[#2B4B8C] font-medium" data-testid={`test-per-test-${test.id}`}>
                        {test.questions_per_test} al azar/prueba
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => openTestDetail(test)} className="border-[#2B4B8C]/10 text-[#2B4B8C] text-xs" data-testid={`view-test-${test.id}`}>
                    <Eye size={14} className="mr-1" /> Ver
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(test)} className="text-[#2B4B8C]/60 hover:text-[#2B4B8C]" data-testid={`edit-test-${test.id}`}>
                    <Pencil size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(test)} className="text-red-400 hover:text-red-500" data-testid={`delete-test-${test.id}`}>
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {visibleTests.length === 0 && (
          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="text-center py-12 text-[#2B4B8C]/40">
              No hay bancos de preguntas. Crea el primero para comenzar.
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2B4B8C]">
              {editingTest ? 'Editar Banco' : 'Nuevo Banco de Preguntas'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateOrUpdate} className="space-y-4" data-testid="test-form">
            <div className="space-y-2">
              <Label className="text-[#2B4B8C]/80">Nombre *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                required
                data-testid="test-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#2B4B8C]/80">Mandante</Label>
              <Select value={formMandante} onValueChange={(v) => {
                setFormMandante(v);
                const currentCourse = courses.find(c => c.id === formData.course_id);
                if (v !== 'all' && currentCourse && currentCourse.client_id !== v) {
                  setFormData(fd => ({ ...fd, course_id: '' }));
                }
              }}>
                <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="test-mandante-select">
                  <SelectValue placeholder="Todos los mandantes" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#2B4B8C]/10 max-h-64">
                  <SelectItem value="all">Todos los mandantes</SelectItem>
                  {mandantes.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#2B4B8C]/80">Curso Asociado *</Label>
              <Select value={formData.course_id} onValueChange={(v) => setFormData({ ...formData, course_id: v })}>
                <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="test-course-select">
                  <SelectValue placeholder="Seleccionar curso" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#2B4B8C]/10 max-h-64">
                  {dialogCourses.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#2B4B8C]/40">Las preguntas disponibles se filtrarán por este curso</p>
            </div>
            <div className="space-y-2">
              <Label className="text-[#2B4B8C]/80">Descripción</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                rows={2}
                data-testid="test-description-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Tiempo (min)</Label>
                <Input
                  type="number"
                  value={formData.time_limit_minutes}
                  onChange={(e) => setFormData({ ...formData, time_limit_minutes: parseInt(e.target.value) || 30 })}
                  className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                  data-testid="test-time-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">% Aprobación</Label>
                <Input
                  type="number"
                  value={formData.passing_percentage}
                  onChange={(e) => setFormData({ ...formData, passing_percentage: parseInt(e.target.value) || 70 })}
                  className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                  data-testid="test-passing-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Intentos Máx.</Label>
                <Input
                  type="number"
                  value={formData.max_attempts}
                  onChange={(e) => setFormData({ ...formData, max_attempts: parseInt(e.target.value) || 3 })}
                  className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                  data-testid="test-attempts-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#2B4B8C]/80">Preguntas al azar por prueba</Label>
              <Input
                type="number"
                min="0"
                value={formData.questions_per_test}
                onChange={(e) => setFormData({ ...formData, questions_per_test: parseInt(e.target.value) || 0 })}
                className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                data-testid="test-questions-per-test-input"
              />
              <p className="text-xs text-[#2B4B8C]/40">
                Cantidad de preguntas que se sortean al azar del banco en cada prueba. Usa <span className="font-medium">0</span> para incluir todas.
                Ej: EAP 1–5 &rarr; 6 preguntas y 100% de aprobación (todas correctas).
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} className="flex-1 border-[#2B4B8C]/10">Cancelar</Button>
              <Button type="submit" className="flex-1 bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="test-submit-btn">
                {editingTest ? 'Actualizar' : 'Crear Banco'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteDeps(null); } }}>
        <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2B4B8C]">Eliminar Banco</DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="delete-confirm-dialog">
            <p className="text-[#2B4B8C]/70">
              ¿Está seguro de eliminar el banco <span className="font-semibold text-[#2B4B8C]">"{deleteTarget?.name}"</span>?
            </p>
            {loadingDeps ? (
              <div className="flex justify-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2BBAA4]" /></div>
            ) : deleteDeps?.has_dependencies ? (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 space-y-2">
                <p className="text-sm font-medium text-red-600">Este test tiene vínculos activos:</p>
                {deleteDeps.courses_count > 0 && (
                  <p className="text-sm text-red-600">
                    Asignado a {deleteDeps.courses_count} curso(s): <span className="font-medium">{deleteDeps.courses.join(', ')}</span>
                  </p>
                )}
                {deleteDeps.evaluations_count > 0 && (
                  <p className="text-sm text-red-600">
                    {deleteDeps.evaluations_count} evaluación(es) creada(s) a partir de este test
                  </p>
                )}
                {deleteDeps.questions_count > 0 && (
                  <p className="text-sm text-red-500/70">
                    {deleteDeps.questions_count} pregunta(s) vinculada(s)
                  </p>
                )}
                <p className="text-xs text-red-500/70 pt-1">Al eliminar se desvincularán todas las relaciones.</p>
              </div>
            ) : (
              <p className="text-sm text-[#2B4B8C]/50">Este test no tiene vínculos activos.</p>
            )}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteDeps(null); }} className="flex-1 border-[#2B4B8C]/10" data-testid="cancel-delete">Cancelar</Button>
              <Button onClick={confirmDelete} className="flex-1 bg-red-500 text-white hover:bg-red-600" data-testid="confirm-delete">Eliminar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
